// AuthContext. Populated from /api/me on app load. Forward-compatible
// with a future login flow — components read identity/scopes here,
// never assume a single user.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api";

export interface Principal {
  name: string;
  kind: string;
  scopes: string[];
}

interface AuthState {
  principal: Principal | null;
  loading: boolean;
  hasScope: (scope: string) => boolean;
}

const AuthContext = createContext<AuthState>({
  principal: null,
  loading: true,
  hasScope: () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.me()
      .then(setPrincipal)
      .catch(() => setPrincipal(null))
      .finally(() => setLoading(false));
  }, []);

  const hasScope = (scope: string) =>
    !!principal && (principal.scopes.includes("*") || principal.scopes.includes(scope));

  return (
    <AuthContext.Provider value={{ principal, loading, hasScope }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
